import { GitCompareArrows, Check } from "lucide-react";
import { toast } from "sonner";
import { useCompare, MAX_COMPARE_MESSAGE } from "@/lib/compare";

/**
 * Add to / Remove from Compare toggle. Two visual variants:
 * `icon` for cards and map sheets, `button` for detail pages.
 */
export function CompareButton({
  id,
  variant = "icon",
  className = "",
}: {
  id: string;
  variant?: "icon" | "button";
  className?: string;
}) {
  const { has, toggle } = useCompare();
  const active = has(id);

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const result = toggle(id);
    if (result.limitReached) {
      toast.error(MAX_COMPARE_MESSAGE);
      return;
    }
    toast.success(result.active ? "Added to compare." : "Removed from compare.");
  };

  if (variant === "button") {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={`flex flex-col items-center justify-center gap-1 rounded-2xl border px-3 py-2.5 text-xs font-semibold transition ${
          active
            ? "border-brand bg-brand-soft text-brown-dark"
            : "border-border bg-card text-brown-dark hover:bg-secondary"
        } ${className}`}
      >
        {active ? <Check className="h-4 w-4" /> : <GitCompareArrows className="h-4 w-4" />}
        {active ? "In Compare" : "Compare"}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={active ? "Remove from compare" : "Add to compare"}
      title={active ? "Remove from compare" : "Add to compare"}
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition ${
        active
          ? "border-brand bg-brand text-brown-dark"
          : "border-border bg-card text-muted-foreground hover:bg-secondary"
      } ${className}`}
    >
      <GitCompareArrows className="h-4 w-4" />
    </button>
  );
}
